import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { Button, Img, Input, Text, Heading } from "../../components";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Link } from "react-router-dom";

export default function FormnewPage() {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    backend: "",
  });
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const captchaRef = useRef(null);
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) return "Email is required";
    if (!regex.test(email)) return "Invalid email format";
    return "";
  };

  const validateName = (name) => {
    const regex = /^[a-zA-ZăâîșțĂÂÎȘȚ\s-]+$/;
    if (!name.trim()) return "Name is required";
    if (!regex.test(name)) return "Invalid characters in name";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "", backend: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nameError = validateName(formData.name);
    const emailError = validateEmail(formData.email);

    const newErrors = {
      name: nameError,
      email: emailError,
      backend: "",
    };

    if (Object.values(newErrors).some((error) => error !== "")) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch(
        "https://fakebackend-vytg.onrender.com/users",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            recaptchaToken,
          }),
        }
      );

      if (response.ok) {
        navigate("/confirmationatsubmitform");
      } else {
        const errorData = await response.json();
        setErrors((prev) => ({
          ...prev,
          backend: errorData.message || "Failed to submit form",
        }));
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrors((prev) => ({
        ...prev,
        backend: "There was an error connecting to the server",
      }));
    }
  };

  const handleCaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  return (
    <>
      <Helmet>
        <title>Lifetoon</title>
        <meta name="description" content="Lifetoon - Join our waitlist" />
      </Helmet>

      <div className="relative h-[100vh] w-full bg-white overflow-hidden flex items-center justify-center">
        {/* Background Images */}
        <Img
          src="images/img_rectangle_18_orange_100.png"
          alt="Decorative background"
          className="absolute right-[-5%] top-0 m-auto h-[690px] w-[53%]"
        />
        <Img
          src="images/img_rectangle_15_orange_accent1.png"
          alt="Decorative background"
          className="absolute bottom-0 left-[-5%] m-auto h-[302px] w-[53%]"
        />

        <Link to="/">
          <Img
            src="images/img_vector_dark_blue_secondary.svg"
            alt="Logo"
            className="absolute top-4 left-4 h-[80px] w-auto object-contain z-20"
          />
        </Link>

        {/* Main Form Container */}
        <div className="container-xs relative z-10 flex justify-center md:px-5">
          <div className="flex w-full justify-center">
            <div className="flex w-[54%] flex-col items-center justify-center gap-[30px] rounded-[24px] bg-white px-14 py-16 shadow-lg md:w-full sm:w-full md:p-5">
              {/* Header Section */}
              <div className="mx-1 flex flex-col items-center gap-1 md:mx-0">
                <Heading
                  size="headings"
                  as="h1"
                  className="text-[36px] font-bold md:text-[34px] sm:text-[32px]"
                >
                  Join the Lifetoon Waitlist
                </Heading>
                <Heading as="h2" className="!font-lora text-[16px] font-normal">
                  Be the first to know when we launch & get exclusive early
                  access!
                </Heading>
              </div>

              {/* Form Section */}
              <form
                onSubmit={handleSubmit}
                className="flex w-[82%] flex-col items-start gap-6 md:w-full"
              >
                {/* Name Field */}
                <div className="flex flex-col items-start gap-1 self-stretch">
                  <Text
                    size="texts"
                    as="p"
                    className="!font-ibmplexsans text-[14px] font-medium !text-gray-700"
                  >
                    Your name&nbsp;
                    <span className="text-orange_accent1">*</span>
                  </Text>
                  <Input
                    shape="round"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="rounded-md border border-gray-300 px-3 h-[2.5vw] md:h-[40px] sm:h-[40px]"
                    maxLength={50}
                  />
                  {errors.name && (
                    <Text className="text-red-500 text-sm">{errors.name}</Text>
                  )}
                </div>

                {/* Email Field */}
                <div className="flex flex-col items-start gap-1 self-stretch">
                  <Text
                    size="texts"
                    as="p"
                    className="!font-ibmplexsans text-[14px] font-medium !text-gray-700"
                  >
                    Your email&nbsp;
                    <span className="text-orange_accent1">*</span>
                  </Text>
                  <Input
                    shape="round"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="rounded-md border border-gray-300 px-3 h-[2.5vw] md:h-[40px] sm:h-[40px]"
                    maxLength={100}
                  />
                  {errors.email && (
                    <Text className="text-red-500 text-sm">{errors.email}</Text>
                  )}
                </div>

                {/* CAPTCHA Section */}
                <div className="w-full">
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    theme="light"
                    size="normal"
                    ref={captchaRef}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex w-full h-[7vw] min-h-[fit-content] items-center justify-center md:h-[100px]">
                  <Button
                    size="lg"
                    rightIcon={
                      <Img
                        src="images/img_arrowright.svg"
                        alt="Arrow Right"
                        className="mb-1 mt-0.5 h-[32px] w-[32px] object-contain transition-all duration-300 ease-in-out"
                      />
                    }
                    className="w-full gap-1.5 rounded-[30px] px-[30px] py-2 font-medium italic shadow-md sm:px-5 
                    bg-orange_accent1 text-dark_blue_secondary
                    border-2 border-solid border-dark_blue_secondary border-b-8
                    hover:bg-yellow-200 hover:border-0 hover:text-dark_blue_secondary
                    transition-all duration-300 ease-in-out sm:min-w-[280px] sm:px-4 sm:text-[14px] sm:py-2"
                  >
                    Join the waitlist today
                  </Button>
                </div>
              </form>

              {/* Backend Error Display */}
              {errors.backend && (
                <div className="mt-4 text-center">
                  <Text className="text-red-600 text-sm font-medium">
                    {errors.backend}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
